/**
 * Pitch Deck Content Configuration
 * ─────────────────────────────────
 * Edit text, stats, tooltips, and media here — no component code changes needed.
 * Each section maps to a slide in the interactive investor pitch.
 */

const pitchDeck = {
  meta: {
    title: "Valpha.io — Investor Pitch",
    subtitle: "All-in-one Trading Studio empowered by AI",
    date: "2025",
    accessCode: "valpha2025", // simple gate — change as needed
  },

  // ───────────────── SECTIONS ─────────────────
  sections: [
    /* ── 0  HERO ── */
    {
      id: "hero",
      type: "hero",
      title: "Valpha.io",
      subtitle: "All-in-one Trading Studio empowered by AI",
      cta: "Scroll to explore ↓",
    },

    /* ── 1  PROBLEM ── */
    {
      id: "problem",
      type: "problem",
      label: "The Problem",
      title: "Retail trading is booming. Outcomes aren't.",
      lead: "The rapid growth of retail trading, combined with a lack of effective guardrails and a fragmented ecosystem of tools, has created a structurally flawed market in which more than 80% of the world's ~20 million active retail traders lose money.",
      painPoints: [
        {
          icon: "ShieldAlert",
          title: "Risk discipline fails",
          body: "Traders know rules; platforms don't enforce them. Volume-driven platforms reward activity, not outcomes.",
          tooltip: "Studies show 78% of retail CFD traders lose money (ASIC, FCA data). Platforms profit from volume, creating a misaligned incentive structure.",
        },
        {
          icon: "Monitor",
          title: "Too many accounts & platforms",
          body: "Multiple brokers/prop UIs → constant switching/re-logins. Cross-account P&L/analysis is manual and error-prone.",
          tooltip: "The average active trader uses 2.3 broker platforms and 4+ separate tools (charting, news, journal, execution). Context-switching costs ~23 minutes per task switch (UCI study).",
        },
        {
          icon: "Brain",
          title: "Mental burden of trading",
          body: "Always-on monitoring → fatigue, FOMO, revenge trades. Manual checklists fail under stress → blow-ups/churn.",
          tooltip: "Behavioural finance research shows emotional decision-making accounts for up to 45% of retail trading losses (Barber & Odean, 2001).",
        },
        {
          icon: "Code",
          title: "Coding barrier for automation",
          body: "Increasing demand for algo trading, but less than 5% of traders can build reliable bots. Deploying/maintaining (VPS, uptime, versions) is heavy and expensive.",
          tooltip: "MQL5 marketplace has 30K+ Expert Advisors, but <5% of retail traders have programming skills. VPS hosting alone costs $20-80/mo per account.",
        },
        {
          icon: "LayoutGrid",
          title: "Fragmented tools",
          body: "Charts/news/bots/analysis live in separate apps. Subscriptions + context switching → expensive, missed entries/exits.",
          tooltip: "A typical active trader spends $150-400/mo on separate tools: TradingView ($60), news feeds ($30-100), journaling ($20), VPS ($40), data feeds ($50+).",
        },
      ],
      stat: {
        value: "80%+",
        label: "of retail traders lose money",
        source: "ASIC, FCA, ESMA regulatory data",
      },
    },

    /* ── 2  SOLUTION ── */
    {
      id: "solution",
      type: "solution",
      label: "The Solution",
      title: "One hub that enforces risk, unifies accounts, collapses tools, and lets traders replicate themselves through automation and AI",
      capabilities: [
        {
          icon: "ShieldCheck",
          title: "Enforced Risk Discipline",
          summary: "Rules set once and enforced before, during, and after trades (loss caps, sizing, limits, cooldowns).",
          detail: "Rules are defined once and automatically enforced pre-trade, during execution, and post-trade. Includes loss caps, automatic position sizing, quantity limits, cooldowns, and audit-tracked, bypass-proof controls.",
          benefit: "Eliminates emotional trading, enforces consistency, and materially reduces catastrophic losses.",
        },
        {
          icon: "Layers",
          title: "Account Orchestration",
          summary: "Trade across MetaTrader, cTrader, and Binance in one place; fast setup at <50% VPS cost.",
          detail: "Unified control of MetaTrader, cTrader, and Binance in a single workspace, with additional brokers supported over time. Fast onboarding at a cost of less than 50% of a standard VPS.",
          benefit: "Reduces operational complexity, lowers infrastructure costs, and simplifies multi-account trading.",
        },
        {
          icon: "BrainCircuit",
          title: 'Co-Pilot to Autopilot ("Trader Twin")',
          summary: "AI learns your style; proposes and (if enabled) executes, human-in-the-loop. Risk guardrails always override.",
          detail: "An AI assistant that learns the trader's behaviour and strategy, proposes trades, and—if enabled—executes them with a human-in-the-loop. Risk guardrails always override the agent; all activity is fully audited and journalled.",
          benefit: "Scales trader decision-making while preserving accountability, control, and compliance.",
        },
        {
          icon: "LayoutGrid",
          title: "All-in-One Workspace",
          summary: "Charts, news, calendar, bots, backtests, journaling in one pane.",
          detail: "Charts, news, economic calendar, bots, backtesting, and journaling in a single pane, with on-demand or scheduled AI-driven technical and fundamental analysis.",
          benefit: "Improves focus and decision quality by eliminating tool-switching and information fragmentation.",
        },
        {
          icon: "Bot",
          title: "No-Code + AI Bot Builder",
          summary: "Natural-language prompts → production-ready strategies in seconds, with native backtesting.",
          detail: "Natural-language prompts generate production-ready strategies in seconds, with native backtesting and safe iteration—no debugging required.",
          benefit: "Dramatically lowers the barrier to automation and accelerates strategy development without engineering overhead.",
        },
      ],
    },

    /* ── 3  BUSINESS MODEL ── */
    {
      id: "business-model",
      type: "businessModel",
      label: "Business Model",
      title: "SaaS tiers + modular add-ons + marketplace",
      tiers: [
        {
          name: "Starter",
          price: "A$65/mo",
          features: ["1 account", "No-code bot builder", "1 live bot"],
          ideal: "Traders with a defined strategy and some trading skills who want to increase returns",
        },
        {
          name: "Pro",
          price: "A$150/mo",
          highlight: true,
          features: ["Everything in Starter", "AI Bot Builder", "3 live bots"],
          ideal: "Traders looking to clone themselves and multiply gains",
        },
        {
          name: "Premium",
          price: "A$350/mo",
          features: ["Everything in Pro", "AI Assistant", "AI Autopilot"],
          ideal: "Maximum flexibility with integrated autopilot to supercharge results",
        },
      ],
      modules: [
        "Extra accounts & live bots",
        "AI assistant modules (prop rules / daily DD)",
        "Backtest credits & priority",
        "News/sentiment & data packs",
        "Template/strategy bundles",
        "Order flow (real volume) data & indicators",
        "Copy/allocate across accounts",
        "Team seats & collaboration",
      ],
      additionalRevenue: [
        {
          title: "Marketplace",
          body: "Creator rev-share for strategies/templates; platform take-rate",
        },
        {
          title: "Prop-firm partnerships",
          body: "Challenge/rule packs; rev-share/bounties",
        },
        {
          title: 'Enterprise "Agentic Stack" (TaaS)',
          body: "Private, governed, auditable agentic trading infrastructure for prop desks, brokers & hedge funds",
          tooltip: "Trading-as-a-Service: the same Valpha stack deployed as a white-label or managed service for institutional clients. This is the long-term expansion play with significantly higher ARPU.",
        },
      ],
    },

    /* ── 4  MARKET OPPORTUNITY ── */
    {
      id: "market",
      type: "market",
      label: "Market Opportunity",
      title: "Massive addressable market with accelerating tailwinds",
      tam: {
        value: "6.79M",
        accounts: 6_790_000,
        label: "active CFD accounts globally (Q4 2024)",
        tooltip: "Retail CFD now accounts for 14.1% of daily global FX turnover — up from 2.7% five years ago. Source: BIS Triennial Survey, Investment Trends.",
      },
      adjacentMarket: {
        value: "40M–70M",
        label: "active crypto users globally",
        tooltip: "Adjacent expansion market. Crypto users share similar automation and risk management needs.",
      },
      sam: {
        value: "2.0M–3.0M",
        accounts: 2_500_000,
        label: "active accounts within Valpha's initial reachable ecosystem",
        tooltip: "SAM consists of users reachable through known channels (MetaTrader, cTrader ecosystems, prop-firm communities, trading education platforms) with 30% assumed payer rate.",
      },
      som: {
        value: "5–10K",
        accountsLow: 5_000,
        accountsHigh: 10_000,
        label: "subscriptions in 12–18 months (≈0.05–0.10% of SAM)",
        tooltip: "Independent target based on rational CAC of A$80 and integrated marketing strategy including creator/affiliate programs, prop-firm pilots, content/SEO, and targeted social media.",
      },
      revenueSizing: {
        currency: "A$",
        payerRate: 0.30,
        arpuScenarios: [55, 70, 110],
        payerRateNote: "TAM$/SAM$ assume 30% payer rate. SOM figures are paid subscribers — no payer‑rate discount.",
      },
    },

    /* ── 5  TRACTION ── */
    {
      id: "traction",
      type: "traction",
      label: "Traction & Validation",
      title: "Built, running, and generating real results",
      stats: [
        { value: "1,000+", label: "test trades executed" },
        { value: "20%", label: "profit return in 2 months (AI Autopilot)" },
        { value: "1.94", label: "win ratio across 128 case-study trades" },
      ],
      builtToday: [
        "MVP live with full MetaTrader & cTrader integrations",
        "Real-time price feeds, historical data, trade execution, risk management, journaling",
        "Charting and indicator library",
        "Customisable UI for the Trading Studio",
        "AI-assisted no-code bot builder and backtesting",
        "AI agentic infrastructure",
        "AI assistant, co-pilot and auto-pilot integrated into the UI",
        "On-demand/scheduled technical & fundamental analysis + trade execution",
        "News and economic calendar",
        "Automated scalable cloud infrastructure",
      ],
      caseStudy: {
        title: "AI Auto-Pilot Case Study",
        body: "A demo agent rigorously tested with historic data and live demo environments executing real trades via cTrader and MetaTrader APIs. 128 test trades executed with a win ratio of 1.94. The AI Auto-pilot module delivered a 20% profit return over a two-month trial period.",
        note: "A copy of the demo account will be supplied with this presentation allowing you to validate effectiveness directly.",
        tooltip: "This case study uses a single trading strategy. With the supplied demo account, you could execute strategies currently deployed by your business and compare results.",
      },
    },

    /* ── 6  UNIT ECONOMICS ── */
    {
      id: "unit-economics",
      type: "unitEconomics",
      label: "Unit Economics",
      title: "Strong fundamentals at Month 12",
      headline: {
        subs: "1,224",
        mrr: "A$117.9K",
        grossMargin: "A$73.9K",
        cac: "A$80",
        payback: "1.4 mo",
        ltvCac: "12.5×",
        netMonthly: "+A$28.9K/m",
        cashPosition: "A$521K",
      },
      tiers: [
        { tier: "Base", price: "A$65", infra: "A$27", gm: "A$38", retention: "12 mo", ltv: "A$456", ltvCac: "5.7×" },
        { tier: "Pro", price: "A$150", infra: "A$55", gm: "A$95", retention: "18 mo", ltv: "A$1,710", ltvCac: "21.4×" },
        { tier: "Premium", price: "A$350", infra: "A$90", gm: "A$260", retention: "24 mo", ltv: "A$6,240", ltvCac: "78.0×" },
      ],
      weightedArpu: "A$96.32",
      weightedGm: "A$60.37",
      weightedLtv: "A$987 (≈12.3× CAC)",
      tooltip: "LTV calculated as GM$/user × average retention months. Weighted by projected M12 tier mix. CAC of A$80 assumes blended acquisition across content marketing, affiliate, and paid channels.",
    },

    /* ── 7  COMPETITIVE LANDSCAPE ── */
    {
      id: "competitive",
      type: "competitive",
      label: "Competitive Landscape",
      title: "No single platform covers the full stack",
      competitors: [
        { name: "Valpha.io", noCode: true, aiAssist: true, backtest: true, marketplace: "roadmap", execution: true, multiAsset: "roadmap", agentic: true, mt5: "roadmap", fundamentals: true, news: true },
        { name: "Tradetron", noCode: true, aiAssist: false, backtest: true, marketplace: true, execution: true, multiAsset: "partial", agentic: false, mt5: false, fundamentals: false, news: false },
        { name: "Capitalise.ai", noCode: true, aiAssist: false, backtest: true, marketplace: false, execution: true, multiAsset: "partial", agentic: false, mt5: false, fundamentals: false, news: false },
        { name: "MetaTrader 5", noCode: false, aiAssist: false, backtest: true, marketplace: true, execution: true, multiAsset: true, agentic: false, mt5: true, fundamentals: "partial", news: true },
        { name: "QuantConnect", noCode: false, aiAssist: false, backtest: true, marketplace: "community", execution: true, multiAsset: true, agentic: false, mt5: false, fundamentals: true, news: "partial" },
        { name: "TradingView", noCode: false, aiAssist: false, backtest: true, marketplace: true, execution: true, multiAsset: true, agentic: false, mt5: false, fundamentals: true, news: true },
      ],
      features: [
        { key: "noCode", label: "No-code Bot Builder" },
        { key: "aiAssist", label: "AI Assist" },
        { key: "backtest", label: "Backtesting" },
        { key: "marketplace", label: "Marketplace" },
        { key: "execution", label: "Execution" },
        { key: "multiAsset", label: "Multi-asset" },
        { key: "agentic", label: "Agentic AI" },
        { key: "mt5", label: "MT5 Integration" },
        { key: "fundamentals", label: "Fundamentals" },
        { key: "news", label: "News/Calendar" },
      ],
      moat: [
        {
          title: "Risk kernel (hard to copy)",
          body: "Pre/in/post-trade controls across all accounts — risk per trade, max consecutive loss, daily DD, cooldowns, time of day — all with audit trails.",
        },
        {
          title: "Agentic execution",
          body: "AI agents that scan markets, identify setups based on user trading style, execute and manage trades within defined risk guardrails.",
        },
        {
          title: "Integration breadth & reliability",
          body: "MT5, cTrader, Binance live; IG/IBKR/FXCM next; FIX bridge path + QA harnesses/monitoring → faster time-to-integrate than DIY.",
        },
        {
          title: "Marketplace flywheel",
          body: "More creators → more templates/bots → more users → more data/performance insights → better ranking & discovery → more creators.",
        },
        {
          title: "Switching costs",
          body: "Unified accounts, rules, bots, history, analytics, and team/RBAC in one place; migrating off breaks workflows and context.",
        },
        {
          title: "Cost & trust advantage",
          body: "<50% of plain VPS cost at similar performance; SOC2 readiness, audit logs, AFSL alignment → partner-friendly.",
        },
      ],
    },

    /* ── 8  TEAM ── */
    {
      id: "team",
      type: "team",
      label: "Team",
      title: "Deep domain expertise across engineering, AI, and growth",
      members: [
        {
          name: "Ehsan Paksima",
          role: "Founder",
          photo: null, // add URL when available
          linkedin: "https://www.linkedin.com/in/ehsanpaksima/",
          highlights: [
            "15 yrs leading tech ops; saved businesses $7M+ via automation & re-architecture",
            "Built high-performing on/offshore teams; shipped complex systems reliably",
            "Active trader; fluent in MT4/5 & cTrader workflows",
            "As Director of Technology, grew a startup +130% profit in 14 months",
            "Solutions architect; led multiple full-stack engineering teams",
          ],
        },
        {
          name: "Erfan Paksima",
          role: "Co-founder (Engineering)",
          photo: null,
          linkedin: "https://www.linkedin.com/in/erfan-paksima-670676147/",
          highlights: [
            "Senior full-stack developer & solutions architect",
            "Expert in Backend, DevOps automation, agentic systems, and ML",
            "Unblocked startups through hard technical obstacles (scaling, data, infra)",
            "10 yrs active trader; deep understanding of retail landscape, players, gaps",
            "Built CI/CD, observability, and cost-efficient cloud patterns",
          ],
        },
        {
          name: "Geoffrey Emerson",
          role: "CMO / CX",
          photo: null,
          linkedin: "https://www.linkedin.com/in/emersongeoffrey/",
          highlights: [
            "AI practitioner — Office of AI NSW and PERCI.IO AI consultancy",
            "Specialises in digital audits, service design, and online marketing",
            "Multi-award winning Head of Digital and creative technologist",
            "Expert in social search consultancy",
            "Experienced in driving brand growth through tailored digital strategy",
          ],
        },
      ],
    },

    /* ── 9  ROADMAP ── */
    {
      id: "roadmap",
      type: "roadmap",
      label: "Roadmap",
      title: "Product Roadmap (12–18 months)",
      phases: [
        {
          label: "Now (0–3 months)",
          subtitle: "Built on top of what we have today",
          items: [
            "UI polish + drawing tools for charting",
            "Billing: accounts & subscriptions",
            "Security: SOC 2 readiness + pen test scheduling",
            "Indicators: expand library",
            "Equities fundamentals",
          ],
          kpis: "50–100 pilot users, crash-free sessions >99%, D7 ≥50%",
        },
        {
          label: "Next (3–9 months)",
          subtitle: "Scale and marketplace",
          items: [
            "Marketplace α (share/sell strategies; reviews; rev share)",
            "Mobile app, education hub",
            "IG, IBKR and FXCM integrations, prop-firm sandbox alignment",
            "AI Agent v2 (full platform integration, multi-agentic trade mgmt, auto-hedging, news/risk events)",
          ],
          kpis: "1–2K paid, +20–30% ARPU uplift, churn <8%/m",
        },
        {
          label: "Later (9–18 months)",
          subtitle: "Enterprise & advanced",
          items: [
            "Marketplace (payments, moderation, curation)",
            "Proprietary ML infra for algo trading",
            "Prop-firm integrations/pilots; broker co-marketing",
            "Backtesting v2 (tick data, walk-forward, Monte Carlo)",
            "Portfolio & P&L Pro; Mobile full; Team/RBAC; Audit logs & security",
          ],
          kpis: "6–10K paid, churn <5%/m, GM ≥70%, 99.9% feed uptime",
        },
      ],
    },

    /* ── 10  RISKS ── */
    {
      id: "risks",
      type: "risks",
      label: "Dependencies & Risks",
      title: "Identified risks with clear mitigations",
      items: [
        {
          risk: "Broker & prop-firm API variability",
          mitigation: "Adapter layer + sandbox testing",
          tooltip: "Each broker has unique API quirks. Our adapter pattern abstracts these away, and every integration is tested against a sandboxed environment before going live.",
        },
        {
          risk: "Market data & backtest quality",
          mitigation: "Licensed feeds, provenance checks, drift monitors, vendor SLAs",
        },
        {
          risk: "Regulatory scope (AFSL / MDA)",
          mitigation: "Launch non-discretionary; Autopilot under CAR; pursue own AFSL (12–18m). Disclosures per RG 244/255, MDA per RG 179.",
          tooltip: "Autopilot = MDA. We stage it: non-discretionary → Corporate Authorised Representative (CAR) → own AFSL. We never hold client funds.",
        },
        {
          risk: "Security & privacy",
          mitigation: "Secrets vaulting, least-privilege tokens, 2FA/SAML, audit logs; external pen-tests; SOC 2 roadmap.",
        },
        {
          risk: "Client funds custody",
          mitigation: "Valpha never holds custody; orders route to client brokers directly.",
          tooltip: "This is a fundamental architectural decision. We are a SaaS overlay — the user's funds remain with their chosen regulated broker at all times.",
        },
      ],
    },

    /* ── 11  THE ASK ── */
    {
      id: "ask",
      type: "ask",
      label: "The Ask",
      title: "Pre-Seed — A$700K (SAFE)",
      raising: "A$700K",
      instrument: "SAFE",
      runway: "18-month runway to GA + 5–10K paying users",
      useOfFunds: [
        { label: "Product & Compliance", pct: 40, detail: "Finish 12–18 month roadmap" },
        { label: "Marketing & Growth", pct: 20, detail: "Creator/affiliate program, prop-firm pilots, content/SEO, targeted social media ads, events; attribution & analytics" },
        { label: "Regulatory & Licensing", pct: 20, detail: "AFSL/partner requirements, legal, audits, governance" },
        { label: "Operations & Support", pct: 20, detail: "Support coverage, cloud/observability, payments, admin" },
      ],
      hiringPlan: [
        { role: "Engineering", detail: "2 BE, 1 FE, 1 Integration (offshore)" },
        { role: "Design", detail: "1 UI/UX designer (onshore)" },
        { role: "Growth", detail: "1 Marketing" },
        { role: "Month 6 adds", detail: "Customer Support + Biz Dev / Sales" },
      ],
    },
  ],
};

export default pitchDeck;
